
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import validationEngine from "devextreme/ui/validation_engine";
import { RegistrationLinkQuestionOptionCreationModel } from "src/app/models/registration-link/registration-link-question-option.model";
import { RegistrationLinkQuestionTypeModel } from "src/app/models/registration-link/registration-link-question-type.model";
import { RegistrationLinkQuestionCreationModel } from "src/app/models/registration-link/registration-link-question.model";

export interface QuestionTrainingGroup {
  id: string;
  name: string;
}

interface TypeItem {
  id: RegistrationLinkQuestionTypeModel;
  text: string;
}

@Component({
  selector: "app-registration-link-questions",
  standalone: false,
  templateUrl: "./registration-link-questions.component.html",
  styleUrls: ["./registration-link-questions.component.scss"],
})
export class RegistrationLinkQuestionsComponent implements OnInit, OnChanges {
  @Input() questions: RegistrationLinkQuestionCreationModel[] = [];
  @Input() trainingGroups: QuestionTrainingGroup[] = [];

  @Output() questionsChange = new EventEmitter<RegistrationLinkQuestionCreationModel[]>();

  readonly types = RegistrationLinkQuestionTypeModel;

  typeItems: TypeItem[] = [];
  textErrors: { message: string }[] = [];
  optionErrors: { message: string }[] = [];

  openQuestion: RegistrationLinkQuestionCreationModel | null = null;
  showErrors = false;

  constructor(
    private readonly translateService: TranslateService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.buildLabels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["questions"]) {
      this.questions ??= [];

      if (!this.openQuestion || !this.questions.includes(this.openQuestion)) {
        this.openQuestion = this.questions[0] ?? null;
      }
    }
  }

  private buildLabels(): void {
    this.typeItems = [
      { id: this.types.SingleChoice, text: this.translate("TYPES.SINGLE") },
      { id: this.types.MultipleChoice, text: this.translate("TYPES.MULTIPLE") },
      { id: this.types.YesNo, text: this.translate("TYPES.YES-NO") },
      { id: this.types.Text, text: this.translate("TYPES.TEXT") },
    ];

    this.textErrors = [{ message: this.translate("QUESTION-REQUIRED") }];
    this.optionErrors = [{ message: this.translate("ANSWER-REQUIRED") }];
  }

  private translate(key: string): string {
    return this.translateService.instant(`REGISTRATION-LINKS.QUESTIONS.${key}`);
  }

  typeLabel(type: RegistrationLinkQuestionTypeModel): string {
    return this.typeItems.find(item => item.id === type)?.text ?? "";
  }

  hasOptions(question: RegistrationLinkQuestionCreationModel): boolean {
    return question.type !== this.types.Text;
  }

  mappedCount(question: RegistrationLinkQuestionCreationModel): number {
    return question.options.filter(option => option.trainingGroupIds?.length).length;
  }

  private newOption(text = ""): RegistrationLinkQuestionOptionCreationModel {
    return {
      id: null,
      text,
      order: 0,
      trainingGroupIds: [],
    };
  }

  private newQuestion(type: RegistrationLinkQuestionTypeModel): RegistrationLinkQuestionCreationModel {
    return {
      id: null,
      text: "",
      hint: null,
      isRequired: false,
      order: 0,
      type,
      options: this.defaultOptions(type),
    };
  }

  private defaultOptions(
    type: RegistrationLinkQuestionTypeModel
  ): RegistrationLinkQuestionOptionCreationModel[] {
    switch (type) {
      case this.types.YesNo:
        return [
          this.newOption(
            this.translateService.instant("COMMON.YES")
          ),
          this.newOption(
            this.translateService.instant("COMMON.NO")
          ),
        ];

      case this.types.Text:
        return [];

      default:
        return [this.newOption(), this.newOption()];
    }
  }

  private commit(): void {
    this.questions.forEach((question, questionIndex) => {
      question.order = questionIndex;

      question.options.forEach((option, optionIndex) => {
        option.order = optionIndex;
      });
    });

    this.questionsChange.emit(this.questions);
  }

  toggle(question: RegistrationLinkQuestionCreationModel): void {
    this.openQuestion = this.openQuestion === question ? null : question;
  }

  add(type: RegistrationLinkQuestionTypeModel): void {
    const question = this.newQuestion(type);

    this.questions = [...this.questions, question];
    this.openQuestion = question;
    this.commit();
  }

  remove(event: { event?: Event }, question: RegistrationLinkQuestionCreationModel): void {
    event.event?.stopPropagation();

    this.questions = this.questions.filter(item => item !== question);

    if (this.openQuestion === question) {
      this.openQuestion = this.questions[0] ?? null;
    }

    this.commit();
  }

  move(
    event: { event?: Event },
    question: RegistrationLinkQuestionCreationModel,
    direction: -1 | 1
  ): void {
    event.event?.stopPropagation();

    const index = this.questions.indexOf(question);
    const newIndex = index + direction;

    if (index < 0 || newIndex < 0 || newIndex >= this.questions.length) {
      return;
    }

    const questions = [...this.questions];

    [questions[index], questions[newIndex]] = [
      questions[newIndex],
      questions[index],
    ];

    this.questions = questions;
    this.commit();
  }

  changeType(
    question: RegistrationLinkQuestionCreationModel,
    type: RegistrationLinkQuestionTypeModel
  ): void {
    if (type == null || type === question.type) {
      return;
    }

    const wasFixedType =
      question.type === this.types.YesNo ||
      question.type === this.types.Text;

    const isFixedType =
      type === this.types.YesNo ||
      type === this.types.Text;

    if (wasFixedType || isFixedType) {
      question.options = this.defaultOptions(type);
    }

    question.type = type;
    this.commit();
  }

  addOption(question: RegistrationLinkQuestionCreationModel): void {
    question.options = [...question.options, this.newOption()];
    this.commit();
  }

  removeOption(
    question: RegistrationLinkQuestionCreationModel,
    option: RegistrationLinkQuestionOptionCreationModel
  ): void {
    if (question.options.length <= 2) {
      return;
    }

    question.options = question.options.filter(item => item !== option);
    this.commit();
  }
  
  validate(): boolean {
    return this.questions.every(q => {
      if (!q.text?.trim()) {
        return false;
      }

      if (q.type !== this.types.Text && q.options.some(o => !o.text?.trim())) {
        return false;
      }

      return true;
    });
  }

  scrollIntoView(): void {
    this.elementRef.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  syncTrainingGroupSelection(treeView: any, ids: string[]): void {
    treeView.instance.unselectAll();
    ids.forEach(id => treeView.instance.selectItem(id));
  }

  onTrainingGroupSelected(
    o: RegistrationLinkQuestionOptionCreationModel, 
    e: any
  ) {
    if (e.node.selected) {
      o.trainingGroupIds = [
        ...o.trainingGroupIds,
        e.node.key,
      ];
    } else {
      o.trainingGroupIds =
        o.trainingGroupIds.filter((id) => id !== e.node.key);
    }
  }
}
